import { useId, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Input } from './Field';

/** Password input with a show/hide toggle. Never pre-fills or generates a value. */
export function PasswordInput(props: { value: string; onChange: (v: string) => void; autoComplete: string; id?: string; minLength?: number; autoFocus?: boolean; 'aria-describedby'?: string; name?: string }) {
  const [show, setShow] = useState(false);
  return (
    <Input
      id={props.id}
      name={props.name}
      type={show ? 'text' : 'password'}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      autoComplete={props.autoComplete}
      minLength={props.minLength}
      autoFocus={props.autoFocus}
      aria-describedby={props['aria-describedby']}
      spellCheck={false}
      trailing={
        <button type="button" className="input-clear" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'} aria-pressed={show}>
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      }
    />
  );
}

/** Mirrors the server policy (apps/api/src/lib/password.ts): the server remains authoritative. */
export function passwordIssues(pw: string): string[] {
  const issues: string[] = [];
  if (pw.length < 12) issues.push('Use at least 12 characters.');
  if (pw.length > 256) issues.push('Use at most 256 characters.');
  if (pw && /^(.)\1+$/.test(pw)) issues.push('Avoid repeated characters.');
  return issues;
}

/** A calm four-step strength hint (length and character variety). Purely advisory. */
export function PasswordStrength({ value }: { value: string }) {
  const id = useId();
  if (!value) return null;
  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(value)).length;
  const score = value.length < 12 ? 1 : value.length >= 20 || variety >= 3 ? (value.length >= 16 && variety >= 3 ? 4 : 3) : 2;
  const label = ['', 'Too short', 'Fair', 'Good', 'Strong'][score];
  return (
    <div className="pw-strength" data-score={score} id={id} aria-live="polite">
      <span className="pw-strength-bars" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={i <= score ? 'on' : undefined} />
        ))}
      </span>
      <span className="pw-strength-label">{label}</span>
    </div>
  );
}
